import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tag } from 'src/components/tag';
import { useAppContext } from 'src/components/app-context';
import { filterChangeAction } from '../../../../actions/filter';
import type { AppState } from 'src/reducers';
import { SelectedFiltersWrapper } from './styles';

type FilterBase = {
  id: string;
  name: string;
  section: {
    name: string;
    unit: string;
  };
};

type SliderFilter = FilterBase & {
  filterType: 'slider' | 'double-slider';
  value: {
    from: number;
    to: number;
  };
};

type BooleanFilter = FilterBase & {
  filterType: 'boolean';
  value: boolean;
};

type ListFilter = FilterBase & {
  filterType: 'list';
  value: unknown;
};

type StringFilter = FilterBase & {
  filterType: 'string';
  value: string;
};

type DoubleSliderFilter = SliderFilter & {
  value: {
    from: {
      from: number;
      to: number;
    };
    to: {
      from: number;
      to: number;
    };
  };
};

type Filter = SliderFilter | BooleanFilter | ListFilter | StringFilter;

function isDoubleSlider(filter: Filter): filter is DoubleSliderFilter {
  return (
    !!filter.value &&
    filter.filterType === 'slider' &&
    (filter.value.from !== null || filter.value.to !== null) &&
    (typeof filter.value.from === 'object' ||
      typeof filter.value.to === 'object')
  );
}

interface SelectedFiltersProps {
  categoryId: string;
  className?: string;
}

const SelectedFilters: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<SelectedFiltersProps>>
> = props => {
  const { t } = useAppContext();
  const { className, categoryId } = props;
  const query = useSelector(
    (state: AppState) => state.areas.category.filter.query
  );
  const selected = useSelector(
    (state: AppState) => state.areas.category.filter.selected
  );
  const dispatch = useDispatch();

  const filters = React.useMemo(() => {
    function onChange(key: string, filter: Filter) {
      let action;
      switch (filter.filterType) {
        case 'list':
          action = { ...filter, checked: false };
          break;
        default: {
          const { [key]: remove, ...filtersToKeep } = query;
          action = filtersToKeep;
        }
      }

      dispatch(filterChangeAction(categoryId, action));
    }

    function renderTag(key: string, filter: Filter) {
      let value: string | null = null;

      switch (filter.filterType) {
        case 'slider': {
          if (isDoubleSlider(filter)) {
            const { from, to } = filter.value;
            const fromRange = from ? `${from.from} - ${from.to}` : '';
            const toRange = to ? `${to.from} - ${to.to}` : '';
            const separator = fromRange && toRange ? '/' : '';

            value = `${fromRange} ${separator} ${toRange}`;
          } else {
            value = filter.value
              ? `${filter.value.from} - ${filter.value.to}`
              : null;
          }
          break;
        }
        case 'boolean':
          value = filter.value
            ? t('filter', 'properties.select-true')
            : t('filter', 'properties.select-false');
          break;
        case 'list':
          value = filter.name;
          break;
        case 'string':
          value = filter.value;
          break;
        default:
        // do nothing;
      }

      return value ? (
        <Tag
          groupName={filter.section && filter.section.name}
          value={value}
          onClick={() => onChange(key, filter)}
        />
      ) : null;
    }

    return Object.keys(selected)
      .filter(key => selected[key])
      .map(key => renderTag(key, selected[key]));
  }, [selected, categoryId, dispatch, query, t]);

  return (
    <SelectedFiltersWrapper className={className}>
      {filters}

      {filters.length > 1 && (
        <Tag
          type="clear"
          value={t('filter', 'button.clear-filter')}
          onClick={() => dispatch(filterChangeAction(props.categoryId, {}))}
        />
      )}
    </SelectedFiltersWrapper>
  );
};

export default SelectedFilters;
